import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import EventEmitter, { DID_MOUNT_COMPONENT } from '../../helpers/EventEmitter'
import { Element } from '../html/element'
import { ScrollComponent } from '../scrollComponent'
import { NoData } from './NoData'
import { TableBody } from './TableBody'
import { TableHead } from './TableHead'
import classes from './index.module.scss'

export const TableContent = ({
  ids,
  getDataFromRedux,
  schema,
  isLoading,
  isNoDataWithHead,
  isStickyFirstCol,
  noDataText,
  renderTBody,
  renderTHead,
  onRowClick,
  isDisplayScroll,
  onRightClick,
  selectedRowId,
  sort,
  changeSort,
  autoHeightMax,
  isCenterNoData,
  noDataPaddingTop,
  isSelectNotIn,
  isNoStickyHead,
  defaultScrollToRight,
}) => {
  const theadRef = useRef()
  const [heightOfTHead, setHeightOfTHead] = useState(0)

  useEffect(() => {
    if (theadRef.current) {
      setHeightOfTHead(theadRef.current.getBoundingClientRect().height)
    }
  }, [])

  useEffect(() => {
    EventEmitter.dispatch(DID_MOUNT_COMPONENT)
  }, [])

  const renderHead = () => {
    return (
      <thead ref={theadRef}>
        {renderTHead ? (
          renderTHead()
        ) : (
          <TableHead
            schema={schema}
            isStickyFirstCol={isStickyFirstCol}
            sort={sort}
            changeSort={changeSort}
            isNoStickyHead={isNoStickyHead}
          />
        )}
      </thead>
    )
  }

  const renderBody = () => {
    if (isNoDataWithHead && ids.length === 0 && !isLoading) {
      return (
        <NoData
          height={autoHeightMax - heightOfTHead}
          colSpan={schema.length}
          value={noDataText}
          isCenterNoData={isCenterNoData}
          noDataPaddingTop={noDataPaddingTop}
        />
      )
    }

    if (renderTBody) {
      return renderTBody()
    }

    return (
      <TableBody
        ids={ids}
        getDataFromRedux={getDataFromRedux}
        schema={schema}
        isStickyFirstCol={isStickyFirstCol}
        onRowClick={onRowClick}
        onRightClick={onRightClick}
        selectedRowId={selectedRowId}
        isSelectNotIn={isSelectNotIn}
      />
    )
  }

  const renderContentScroll = () => {
    return (
      <Element
        elementName="table"
        className={classes.table}
        style={{ backgroundColor: '#FFFFFF' }}
      >
        {renderHead()}
        {renderBody()}
      </Element>
    )
  }

  return (
    <>
      {isDisplayScroll ? (
        <ScrollComponent
          verticalTrackTop={heightOfTHead}
          autoHeight
          autoHeightMax={autoHeightMax}
          defaultScrollToRight={defaultScrollToRight}
        >
          {renderContentScroll()}
        </ScrollComponent>
      ) : (
        <>{renderContentScroll()}</>
      )}
    </>
  )
}

TableContent.propTypes = {
  ids: PropTypes.array.isRequired,
  getDataFromRedux: PropTypes.func.isRequired,
  schema: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  isNoDataWithHead: PropTypes.bool,
  isStickyFirstCol: PropTypes.bool,
  noDataText: PropTypes.string,
  renderTBody: PropTypes.func,
  renderTHead: PropTypes.func,
  onRowClick: PropTypes.func,
  isDisplayScroll: PropTypes.bool,
  onRightClick: PropTypes.func,
  autoMaxHeight: PropTypes.number,
  selectedRowId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),
  sort: PropTypes.shape({
    sortBy: PropTypes.string,
    sortDirection: PropTypes.string,
  }),
  changeSort: PropTypes.func,
  isCenterNoData: PropTypes.bool,
  noDataPaddingTop: PropTypes.number,
  defaultScrollToRight: PropTypes.bool,
}

TableContent.defaultProps = {
  isLoading: false,
  isNoDataWithHead: false,
  isStickyFirstCol: false,
  noDataText: 'common.NO_DATA',
  renderPagination: () => {},
  onRowClick: () => {},
  size: {},
  isDisplayScroll: PropTypes.bool,
  isCenterNoData: true,
  noDataPaddingTop: 80,
  defaultScrollToRight: false,
}
